// src/components/dashboards/FacultyCMReviews.jsx
import React, { useState, useEffect } from 'react';
import { facultyThesisProposalAPI, facultyEthicsAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

const ACCENT = 'rgb(79, 70, 229)'; // Modern Indigo

const labelStyle = {
  fontSize: '11px',
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  marginBottom: '4px',
  display: 'block',
};

const getFileIcon = (url) => {
  if (!url) return '📎';
  if (url.endsWith('.pdf')) return '📕';
  if (/\.(jpg|jpeg|png|gif|webp)/i.test(url)) return '🖼';
  return '📄';
};

const getStatusBadgeStyle = (status) => {
  const s = (status || '').toUpperCase();
  switch (s) {
    case 'DRAFT':
      return { bg: '#f1f5f9', c: '#64748b' };
    case 'SUBMITTED':
    case 'UNDER_REVIEW':
      return { bg: '#dbeafe', c: '#1d4ed8' };
    case 'CHANGES_REQUESTED':
      return { bg: '#fef3c7', c: '#d97706' };
    case 'DRAFT_APPROVED':
      return { bg: '#dcfce7', c: '#15803d' };
    case 'FINAL_SUBMITTED':
      return { bg: '#e0e7ff', c: '#4338ca' };
    case 'FACULTY_APPROVED':
    case 'READY_FOR_COE_EVALUATION':
      return { bg: '#d1fae5', c: '#065f46' };
    case 'REJECTED':
      return { bg: '#fee2e2', c: '#991b1b' };
    default:
      return { bg: '#f1f5f9', c: '#374151' };
  }
};

const FacultyCMReviews = ({ mode }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [selectedItem, setSelectedItem] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    fetchItems();
    setSelectedItem(null);
    setRemarks('');
  }, [mode, statusFilter]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      let data;
      if (mode === 'thesis') {
        data = await facultyThesisProposalAPI.getProposals(statusFilter);
      } else {
        data = await facultyEthicsAPI.getSubmissions(statusFilter);
      }
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
    return `${backendBase}${url}`;
  };

  const handleReview = async (action) => {
    if ((action === 'REJECT' || action === 'REQUEST_CHANGES') && !remarks.trim()) {
      notify.success('Remarks are required for rejecting or requesting changes.');
      return;
    }

    const actionLabels = {
      'APPROVE_DRAFT': 'approve the draft of',
      'APPROVE': 'final approve',
      'REQUEST_CHANGES': 'request changes for',
      'REJECT': 'reject'
    };
    
    if (!await confirmDialog(`Are you sure you want to ${actionLabels[action] || action.toLowerCase()} this submission?`)) {
      return;
    }

    setReviewing(true);
    try {
      if (mode === 'thesis') {
        await facultyThesisProposalAPI.reviewProposal(selectedItem.id, action.toLowerCase(), remarks.trim());
      } else {
        await facultyEthicsAPI.reviewSubmission(selectedItem.id, action.toLowerCase(), remarks.trim());
      }
      notify.success(`Review decision submitted successfully.`);
      setSelectedItem(null);
      setRemarks('');
      await fetchItems();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to submit review decision.');
    } finally {
      setReviewing(false);
    }
  };

  const isThesis = mode === 'thesis';

  const formatReviewDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* Top Filter Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
            {isThesis ? 'Thesis Submission Review Panel' : 'Ethics Clearance Review Panel'}
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
            {isThesis 
              ? 'Review and approve PG Thesis drafts and final dissertation uploads.' 
              : 'Review, approve, or request changes for student research ethics study proposals.'
            }
          </p>
        </div>

        {/* Status Filter buttons */}
        <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          {['PENDING', 'APPROVED', 'CHANGES_REQUESTED', 'REJECTED'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              style={{
                padding: '8px 16px',
                border: 'none',
                background: statusFilter === status ? ACCENT : '#fff',
                color: statusFilter === status ? '#fff' : '#475569',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                borderRight: '1px solid #e2e8f0',
                transition: 'all 0.15s ease',
              }}
            >
              {status === 'PENDING' ? 'Pending Review' : status.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Main Table View */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
            <div className="spinner" style={{ margin: '0 auto 10px', width: '30px', height: '30px', border: `3px solid ${ACCENT}22`, borderTopColor: ACCENT, borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            Loading submissions...
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f8fafc', color: '#64748b', fontSize: '14px' }}>
            No {statusFilter.toLowerCase().replace('_', ' ')} submissions found.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569' }}>Student Name</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569' }}>Reg Number</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569' }}>{isThesis ? 'Thesis Title' : 'Study Title'}</th>
                  
                  {isThesis ? (
                    <>
                      <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569' }}>Linked Ethics Study</th>
                      <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569' }}>Guide / Faculty</th>
                      <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569' }}>Draft Sub Date</th>
                      <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569' }}>Final Sub Date</th>
                    </>
                  ) : (
                    <>
                      <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569' }}>Study Type</th>
                      <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569' }}>Submission Date</th>
                    </>
                  )}
                  
                  <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569' }}>Status</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const badgeStyle = getStatusBadgeStyle(item.status);
                  const studyTypeLabel = item.study_type ? item.study_type.replace('_', ' ') : 'N/A';
                  return (
                    <tr 
                      key={item.id} 
                      style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <td style={{ padding: '14px 18px', fontWeight: 600, color: '#0f172a' }}>{item.student_name || 'N/A'}</td>
                      <td style={{ padding: '14px 18px', color: '#475569' }}>{item.student_id_num || 'N/A'}</td>
                      <td style={{ padding: '14px 18px', color: '#334155', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={isThesis ? item.title : item.project_title}>
                        {isThesis ? item.title : item.project_title}
                      </td>

                      {isThesis ? (
                        <>
                          <td style={{ padding: '14px 18px', color: '#64748b', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.ethics_study_title}>
                            {item.ethics_study_title || 'N/A'}
                          </td>
                          <td style={{ padding: '14px 18px', color: '#475569' }}>{item.guide_name || 'N/A'}</td>
                          <td style={{ padding: '14px 18px', color: '#64748b' }}>{formatReviewDate(item.draft_submitted_at)}</td>
                          <td style={{ padding: '14px 18px', color: '#64748b' }}>{formatReviewDate(item.final_submitted_at)}</td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '14px 18px', color: '#475569' }}>
                            <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'capitalize', padding: '3px 8px', borderRadius: '4px', background: '#f1f5f9', color: '#475569' }}>
                              {studyTypeLabel.toLowerCase()}
                            </span>
                          </td>
                          <td style={{ padding: '14px 18px', color: '#64748b' }}>{formatReviewDate(item.submitted_at)}</td>
                        </>
                      )}

                      <td style={{ padding: '14px 18px' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '11px',
                          fontWeight: 700,
                          backgroundColor: badgeStyle.bg,
                          color: badgeStyle.c,
                          whiteSpace: 'nowrap'
                        }}>
                          {item.status ? item.status.replace(/_/g, ' ') : 'N/A'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                        <button
                          onClick={() => {
                            setSelectedItem(item);
                            setRemarks(item.faculty_remarks || item.remarks || '');
                          }}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: `1px solid ${ACCENT}`,
                            background: '#fff',
                            color: ACCENT,
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontSize: '12px',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = ACCENT;
                            e.currentTarget.style.color = '#fff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.color = ACCENT;
                          }}
                        >
                          {statusFilter === 'PENDING' ? 'Review' : 'View'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Overlay Modal */}
      {selectedItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px'
        }} onClick={() => setSelectedItem(null)}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '780px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column'
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid #f1f5f9'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  {isThesis ? 'Thesis submission review details' : 'Ethics Clearance Proposal Review'}
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>
                  Submitted by {selectedItem.student_name} ({selectedItem.student_id_num || 'N/A'})
                </p>
              </div>
              <button
                onClick={() => setSelectedItem(null)}
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: '18px',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Title Section */}
              <div>
                <span style={labelStyle}>{isThesis ? 'Thesis Title' : 'Research Study Title'}</span>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                  {isThesis ? selectedItem.title : selectedItem.project_title}
                </div>
              </div>

              {/* Grid Metadata */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <span style={labelStyle}>Student Details</span>
                  <div style={{ fontSize: '13px', color: '#334155' }}>
                    <strong>{selectedItem.student_name}</strong>
                    <div style={{ color: '#64748b', marginTop: '2px' }}>
                      Course: {selectedItem.student_course || 'PG'}
                    </div>
                  </div>
                </div>
                <div>
                  <span style={labelStyle}>Department</span>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                    {selectedItem.department_name || 'Community Medicine'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <span style={labelStyle}>{isThesis ? 'Guide Name' : 'Assigned Guide / Faculty'}</span>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                    {selectedItem.guide_name || selectedItem.faculty_name || 'N/A'}
                  </div>
                </div>
                <div>
                  <span style={labelStyle}>{isThesis ? 'Linked Ethics Study' : 'Study Type'}</span>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: isThesis ? ACCENT : '#334155' }}>
                    {isThesis 
                      ? (selectedItem.ethics_study_title || 'N/A') 
                      : (selectedItem.study_type ? selectedItem.study_type.replace('_', ' ') : 'N/A')
                    }
                  </div>
                </div>
              </div>

              {/* Description / Abstract */}
              <div>
                <span style={labelStyle}>{isThesis ? 'Abstract / description' : 'Study Description'}</span>
                <div style={{
                  fontSize: '13px',
                  color: '#475569',
                  background: '#f8fafc',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '160px',
                  overflowY: 'auto',
                  lineHeight: 1.5
                }}>
                  {selectedItem.description || selectedItem.abstract || 'No description provided.'}
                </div>
              </div>

              {/* Documents Display */}
              {isThesis ? (
                <div>
                  <span style={labelStyle}>Thesis Uploaded Documents</span>
                  {selectedItem.documents && selectedItem.documents.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                      {selectedItem.documents.map(doc => (
                        <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                            <span style={{ fontSize: '16px' }}>{getFileIcon(doc.file_name)}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '280px', color: '#334155' }}>
                              {doc.file_name} ({doc.document_type || 'Draft'})
                            </span>
                          </div>
                          <a 
                            href={getFullUrl(doc.file_url || doc.file)} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={{ textDecoration: 'none', color: ACCENT, fontSize: '12px', fontWeight: 700 }}
                          >
                            View File
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>No documents uploaded.</div>
                  )}
                </div>
              ) : (
                <div>
                  <span style={labelStyle}>Uploaded Study Proposal Document</span>
                  {selectedItem.reference_document ? (
                    <a
                      href={getFullUrl(selectedItem.reference_document)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '13px',
                        color: ACCENT,
                        textDecoration: 'none',
                        fontWeight: 600,
                        background: '#f5f3ff',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        border: '1px solid #ddd6fe'
                      }}
                    >
                      {getFileIcon(selectedItem.reference_document)} View Reference File
                    </a>
                  ) : (
                    <div style={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>No reference document uploaded.</div>
                  )}
                </div>
              )}

              {/* Status and Action Forms */}
              {statusFilter === 'PENDING' ? (
                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ ...labelStyle, marginBottom: '6px' }}>Review Remarks (Required if rejecting or requesting corrections)</label>
                    <textarea
                      value={remarks}
                      onChange={e => setRemarks(e.target.value)}
                      placeholder="Enter reviewer feedback, suggestions, or reason for decision..."
                      style={{
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        width: '100%',
                        boxSizing: 'border-box',
                        minHeight: '80px',
                        fontFamily: 'inherit',
                        lineHeight: 1.4,
                      }}
                    />
                  </div>

                  {/* Context-aware buttons */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '10px' }}>
                    <button
                      onClick={() => handleReview('REJECT')}
                      disabled={reviewing}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#ef4444',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      ✕ Reject
                    </button>
                    
                    <button
                      onClick={() => handleReview('REQUEST_CHANGES')}
                      disabled={reviewing}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#ea580c',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      ✏️ Request Corrections
                    </button>

                    {isThesis ? (
                      selectedItem.status === 'FINAL_SUBMITTED' ? (
                        <button
                          onClick={() => handleReview('APPROVE')}
                          disabled={reviewing}
                          style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#16a34a',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          ✓ Final Approve
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReview('APPROVE_DRAFT')}
                          disabled={reviewing}
                          style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#2563eb',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          ✓ Approve Draft
                        </button>
                      )
                    ) : (
                      <button
                        onClick={() => handleReview('APPROVE')}
                        disabled={reviewing}
                        style={{
                          padding: '10px 16px',
                          borderRadius: '8px',
                          border: 'none',
                          background: '#16a34a',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '13px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        ✓ Approve
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '20px', fontSize: '13px', color: '#475569', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div>
                    <strong>Reviewed By:</strong> {selectedItem.faculty_reviewer_name || selectedItem.reviewed_by_name || 'Faculty Member'}
                  </div>
                  {selectedItem.reviewed_at || selectedItem.faculty_approved_at ? (
                    <div>
                      <strong>Reviewed At:</strong> {formatReviewDate(selectedItem.reviewed_at || selectedItem.faculty_approved_at)}
                    </div>
                  ) : null}
                  {(selectedItem.faculty_remarks || selectedItem.remarks) && (
                    <div style={{ marginTop: '6px' }}>
                      <strong>Reviewer Decision Remarks:</strong>
                      <div style={{ background: '#f8fafc', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', color: '#334155', marginTop: '6px', lineHeight: 1.4 }}>
                        {selectedItem.faculty_remarks || selectedItem.remarks}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default FacultyCMReviews;
